/**
 * Centered confirmation modals (Family 2).
 *
 * Use StyledPopconfirm for inline button-attached actions (Family 1).
 * Use UpgradeMessage for plan-gate / upsell prompts (Family 3).
 */
import { Modal, Row, Space, Typography } from 'antd';
import type { ModalProps } from 'antd';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { ContinueButton, ScopeButton } from './buttons';

const { Text } = Typography;

export type ConfirmModalSize = 'compact' | 'standard';

export type ConfirmModalActionVariant = 'default' | 'primary' | 'warning' | 'danger';

export interface ConfirmModalAction {
  label: string;
  onClick: () => void;
  variant?: ConfirmModalActionVariant;
}

export interface ConfirmModalProps extends Omit<ModalProps, 'footer' | 'title'> {
  title: React.ReactNode;
  size?: ConfirmModalSize;
  primaryAction?: ConfirmModalAction;
  secondaryAction?: ConfirmModalAction;
  cancelAction?: ConfirmModalAction;
  showCancel?: boolean;
  children?: React.ReactNode;
  /** Phone / PWA bottom-sheet layout (stacked full-width actions). */
  pwaMobileNav?: boolean;
}

const compactButtonStyles = css`
  width: 107px;
  height: 25px;
  font-size: 12px;
  line-height: 12px;
`;

const standardButtonStyles = css`
  width: fit-content;
  height: 40px;
`;

const warningDangerButtonStyles = css`
  .ant-modal-footer
    .ant-btn-default.ant-btn-dangerous:not(:disabled):not(.ant-btn-disabled),
  .ant-modal-footer
    .ant-btn-default.ant-btn-color-dangerous:not(:disabled):not(.ant-btn-disabled) {
    background-color: var(--btn-warning-bg, #fce7e7) !important;
    color: var(--btn-warning-text, #d13131) !important;
    border-color: var(--btn-warning-bg, #fce7e7) !important;
    box-shadow: none;
  }

  .ant-modal-footer
    .ant-btn-default.ant-btn-dangerous:not(:disabled):not(.ant-btn-disabled):hover,
  .ant-modal-footer
    .ant-btn-default.ant-btn-dangerous:not(:disabled):not(.ant-btn-disabled):focus,
  .ant-modal-footer
    .ant-btn-default.ant-btn-color-dangerous:not(:disabled):not(.ant-btn-disabled):hover,
  .ant-modal-footer
    .ant-btn-default.ant-btn-color-dangerous:not(:disabled):not(.ant-btn-disabled):focus {
    background-color: var(--btn-warning-text, #d13131) !important;
    color: var(--btn-warning-bg, #fce7e7) !important;
    border-color: var(--btn-warning-bg, #fce7e7) !important;
    box-shadow: none;
  }
`;

const sharedModalStyles = css`
  .ant-modal-content {
    border-radius: 20px !important;
    overflow: hidden;
  }

  .ant-modal-header {
    border-radius: 20px 20px 0 0;
    padding: 20px !important;
  }

  .ant-modal-body {
    padding: 0 20px 20px !important;
  }

  .ant-modal-title,
  .ant-modal-body p {
    color: var(--color-text-dark, #042a2a) !important;
    text-align: center !important;
  }

  .ant-modal-footer {
    padding: 0 20px 20px !important;
    text-align: center !important;
    border: none !important;
  }

  .ant-modal-footer .ant-btn-primary:hover {
    background-color: var(--btn-accent-bg, #ffbc3e) !important;
    color: var(--color-text-dark, #042a2a) !important;
    border-color: var(--color-primary-hover, #64bbbd) !important;
    box-shadow: none;
  }

  .ant-modal-footer .ant-btn-default:hover {
    box-shadow: none;
  }

  ${warningDangerButtonStyles}
`;

export const StyledConfirmModalCompact = styled(Modal)`
  ${sharedModalStyles}

  .ant-modal-footer .ant-btn {
    ${compactButtonStyles}
    font-weight: 600;
    border-radius: 50px;
  }
`;

export const StyledConfirmModalStandard = styled(Modal)`
  ${sharedModalStyles}

  .ant-modal-footer .ant-btn {
    ${standardButtonStyles}
    font-weight: 600;
    border-radius: 50px;
  }
`;

/** @deprecated Use ConfirmModal or StyledConfirmModalCompact. */
export const StyledUnsavedChangesModal = StyledConfirmModalCompact;

const SIZE_WIDTH: Record<ConfirmModalSize, number> = {
  compact: 420,
  standard: 800,
};

function resolveScopeButtonType(
  variant: ConfirmModalActionVariant | undefined,
  fallback: 'default' | 'primary'
): 'default' | 'primary' | 'warning-red' {
  if (variant === 'warning' || variant === 'danger') return 'warning-red';
  if (variant === 'primary') return 'primary';
  return fallback;
}

export const ConfirmModalFooter: React.FC<{
  size: ConfirmModalSize;
  primaryAction?: ConfirmModalAction;
  secondaryAction?: ConfirmModalAction;
  cancelAction?: ConfirmModalAction;
  showCancel?: boolean;
  pwaMobileNav?: boolean;
}> = ({
  size,
  primaryAction,
  secondaryAction,
  cancelAction,
  showCancel = true,
  pwaMobileNav = false,
}) => {
  const isCompact = size === 'compact';
  const buttonWidth = pwaMobileNav
    ? '100%'
    : isCompact
      ? '107px'
      : 'fit-content';
  const buttonHeight = pwaMobileNav ? 'auto' : isCompact ? '25px' : '40px';
  const fontSize = isCompact ? '12px' : undefined;
  const buttonStyle = pwaMobileNav
    ? { borderRadius: 50, minHeight: 40, whiteSpace: 'normal' as const }
    : isCompact
      ? { borderRadius: 50 }
      : undefined;

  const renderAction = (
    action: ConfirmModalAction,
    fallbackType: 'default' | 'primary',
    useContinueButton = false
  ) => {
    const type = resolveScopeButtonType(action.variant, fallbackType);
    const commonProps = {
      width: buttonWidth,
      height: buttonHeight,
      fontSize,
      style: buttonStyle,
      onClick: action.onClick,
    };

    if (useContinueButton) {
      return (
        <ContinueButton key={action.label} {...commonProps}>
          {action.label}
        </ContinueButton>
      );
    }

    return (
      <ScopeButton
        key={action.label}
        type={type}
        danger={action.variant === 'danger'}
        {...commonProps}
      >
        {action.label}
      </ScopeButton>
    );
  };

  return (
    <Space
      direction={pwaMobileNav ? 'vertical' : 'horizontal'}
      size={pwaMobileNav ? 8 : undefined}
      style={pwaMobileNav ? { width: '100%' } : undefined}
    >
      {showCancel &&
        cancelAction &&
        renderAction(
          { ...cancelAction, variant: cancelAction.variant ?? 'default' },
          'default'
        )}
      {secondaryAction &&
        renderAction(
          secondaryAction,
          'default',
          size === 'standard' && secondaryAction.variant !== 'primary'
        )}
      {primaryAction && renderAction(primaryAction, 'primary', false)}
    </Space>
  );
};

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  size = 'compact',
  title,
  primaryAction,
  secondaryAction,
  cancelAction,
  showCancel = true,
  children,
  centered = true,
  width,
  onCancel,
  className,
  style,
  pwaMobileNav = false,
  zIndex,
  destroyOnClose,
  ...modalProps
}) => {
  const { t } = useTranslation();
  const ModalComponent =
    size === 'compact' ? StyledConfirmModalCompact : StyledConfirmModalStandard;

  const resolvedZIndex =
    zIndex ?? (pwaMobileNav ? 1300 : undefined);

  const resolvedCancelAction: ConfirmModalAction | undefined =
    cancelAction ??
    (onCancel
      ? {
          label: t('common.cancel'),
          onClick: onCancel as () => void,
        }
      : undefined);

  return (
    <ModalComponent
      title={title}
      centered={pwaMobileNav ? false : centered}
      width={pwaMobileNav ? '100%' : width ?? SIZE_WIDTH[size]}
      zIndex={resolvedZIndex}
      destroyOnClose={destroyOnClose ?? pwaMobileNav}
      onCancel={onCancel}
      className={[
        pwaMobileNav ? 'scope-pwa-modal scope-pwa-confirm-modal' : undefined,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      style={
        pwaMobileNav
          ? {
              maxWidth: '100%',
              width: '100%',
              top: 'auto',
              bottom: 0,
              margin: 0,
              paddingBottom: 0,
              ...style,
            }
          : style
      }
      footer={
        primaryAction || secondaryAction ? (
          <ConfirmModalFooter
            size={size}
            primaryAction={primaryAction}
            secondaryAction={secondaryAction}
            cancelAction={resolvedCancelAction}
            showCancel={showCancel && !!resolvedCancelAction}
            pwaMobileNav={pwaMobileNav}
          />
        ) : null
      }
      {...modalProps}
    >
      {children}
    </ModalComponent>
  );
};

/** Body content for Redux global confirm modals (rendered inside ModalWindowContainer). */
export const ConfirmModalBody: React.FC<{
  description: React.ReactNode;
  size?: ConfirmModalSize;
  primaryAction?: ConfirmModalAction;
  secondaryAction?: ConfirmModalAction;
  cancelAction?: ConfirmModalAction;
  showCancel?: boolean;
}> = ({
  description,
  size = 'standard',
  primaryAction,
  secondaryAction,
  cancelAction,
  showCancel = true,
}) => (
  <>
    <Row justify="center">
      <Text style={{ textAlign: 'center', width: '100%' }}>{description}</Text>
    </Row>
    {(primaryAction || secondaryAction) && (
      <Row justify="center" style={{ paddingTop: '20px' }}>
        <ConfirmModalFooter
          size={size}
          primaryAction={primaryAction}
          secondaryAction={secondaryAction}
          cancelAction={cancelAction}
          showCancel={showCancel}
        />
      </Row>
    )}
  </>
);
